import { getItem, getTrail } from '@shefira/shared';
import type Phaser from 'phaser';

import { puffOf, spriteOf, type TrailParticle } from './trail-particles.js';

/**
 * Every footstep trail in the scene (ADR 0073): tiny sprites above the ground
 * and its cover (-500), under the move marker and every standing sprite,
 * since a wake lies on the ground behind its walker. Images are pooled: a
 * crowded square leaves hundreds of them a second.
 */
const TRAIL_DEPTH = -450;

/** Enough for a crowded square; past it the oldest sprites go first. */
const MAX_PARTICLES = 600;

/** A sheet as the manifest names it: its atlas and the prefix of its images. */
export type SheetOf = (key: string) => { atlas: string; prefix: string } | undefined;

export class FootstepTrails {
  private readonly particles: TrailParticle[] = [];
  private readonly pool: Phaser.GameObjects.Image[] = [];
  /** Where each walker last left a puff, and how far they walked since. */
  private readonly walkers = new Map<string, { x: number; y: number; walked: number }>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sheetOf: SheetOf,
  ) {}

  /**
   * A walker drawn at `feet` this frame, wearing the trail template
   * `templateId` (empty for none): a puff every `every` pixels walked.
   */
  follow(key: string, templateId: string, feet: { x: number; y: number }, now: number): void {
    const trail = templateId === '' ? undefined : getTrail(getItem(templateId)?.trail ?? '');
    const last = this.walkers.get(key);
    if (trail === undefined || this.sheetOf(trail.sprite) === undefined) {
      this.walkers.delete(key);
      return;
    }
    if (last === undefined) {
      this.walkers.set(key, { x: feet.x, y: feet.y, walked: 0 });
      return;
    }
    last.walked += Math.hypot(feet.x - last.x, feet.y - last.y);
    last.x = feet.x;
    last.y = feet.y;
    // A walker who snapped across the map (a portal) leaves no streak.
    if (last.walked > 64) last.walked = 0;
    while (last.walked >= trail.every) {
      last.walked -= trail.every;
      this.particles.push(...puffOf(trail, feet, now));
    }
    if (this.particles.length > MAX_PARTICLES) {
      this.particles.splice(0, this.particles.length - MAX_PARTICLES);
    }
  }

  forget(key: string): void {
    this.walkers.delete(key);
  }

  draw(now: number): void {
    let kept = 0;
    for (const particle of this.particles) {
      const sprite = spriteOf(particle, now);
      const sheet = this.sheetOf(particle.trail.sprite);
      if (sprite === null || sheet === undefined) continue;
      const image = this.imageAt(kept);
      this.particles[kept] = particle;
      kept += 1;
      image.setTexture(sheet.atlas, `${sheet.prefix}/idle/${String(sprite.frame)}`);
      // Top-left corner on a whole pixel: an odd-sized sprite centred on its
      // middle would land on half a pixel (CLAUDE.md rule 6).
      image.setPosition(
        sprite.x - Math.floor(image.width / 2),
        sprite.y - Math.floor(image.height / 2),
      );
      image.setAlpha(sprite.alpha).setVisible(true);
    }
    this.particles.length = kept;
    for (let index = kept; index < this.pool.length; index += 1) {
      this.pool[index]?.setVisible(false);
    }
  }

  destroy(): void {
    for (const image of this.pool) image.destroy();
    this.pool.length = 0;
  }

  private imageAt(index: number): Phaser.GameObjects.Image {
    const pooled = this.pool[index];
    if (pooled !== undefined) return pooled;
    const image = this.scene.add.image(0, 0, '__DEFAULT').setOrigin(0, 0).setDepth(TRAIL_DEPTH);
    this.pool.push(image);
    return image;
  }
}
